import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import Pagination from '../../components/Pagination';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';
import { Pencil, Trash2, Star, Sparkles, Loader2, ExternalLink, CheckCircle } from 'lucide-react';

const AdminBlogsContent = () => {
  const { isCollapsed } = useSidebar();
  const [blogs, setBlogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentCategories, setContentCategories] = useState([]);
  const [contentTags, setContentTags] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image: '',
    contentCategory: '',
    contentTags: [],
    author: '',
    status: 'draft',
    featured: false,
  });
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);

  // Facebook Import State
  const [showFbModal, setShowFbModal] = useState(false);
  const [fbUrl, setFbUrl] = useState('');
  const [fbStatus, setFbStatus] = useState('published');
  const [fbLoading, setFbLoading] = useState(false);
  const [fbStep, setFbStep] = useState('');
  const [fbSuccessResult, setFbSuccessResult] = useState(null);

  const handleFbImport = async (isPreview = false) => {
    if (!fbUrl.trim()) {
      showError('Please enter a Facebook URL');
      return;
    }
    if (!fbUrl.includes('facebook.com') && !fbUrl.includes('fb.watch')) {
      showError('Please enter a valid Facebook URL (Reel, Video, or Post)');
      return;
    }

    setFbLoading(true);
    setFbSuccessResult(null);
    setFbStep(isPreview ? 'Extracting media & drafting article...' : 'Downloading audio, transcribing speech & publishing blog...');

    try {
      const res = await api.post('/blogs/import-facebook', {
        url: fbUrl.trim(),
        status: fbStatus,
        preview: isPreview,
      }, { timeout: 180000 });

      if (res.data?.success) {
        if (isPreview && res.data.data) {
          const d = res.data.data;
          setFormData({
            title: d.title || '',
            content: d.content || '',
            excerpt: d.excerpt || '',
            image: d.image || '',
            contentCategory: '',
            contentTags: [],
            author: d.author || '',
            status: d.status || 'draft',
            featured: false,
          });
          setSelectedTagIds([]);
          setImageFile(null);
          setImagePreview(d.image || null);
          setEditingId(null);
          setShowFbModal(false);
          setShowModal(true);
          showSuccess('Article drafted from Facebook! You can now review and save.');
        } else if (res.data.blog) {
          setFbSuccessResult(res.data.blog);
          showSuccess('Blog published successfully from Facebook!');
          fetchBlogs();
        }
      } else {
        showError(res.data?.message || 'Failed to import from Facebook');
      }
    } catch (err) {
      console.error(err);
      const data = err.response?.data;
      const errMsg = data?.details
        ? `${data.message || 'Error'}: ${data.details.trim()}`
        : (data?.message || err.message || 'Error processing Facebook post');
      showError(errMsg);
    } finally {
      setFbLoading(false);
      setFbStep('');
    }
  };

  useEffect(() => {
    fetchDropdowns();
  }, []);
  useEffect(() => {
    fetchBlogs();
  }, [statusFilter, categoryFilter, tagFilter, searchTerm, currentPage, limit]);

  const fetchDropdowns = async () => {
    try {
      const [cRes, tRes] = await Promise.all([
        api.get('/content-categories').catch(() => ({ data: [] })),
        api.get('/content-tags').catch(() => ({ data: [] })),
      ]);
      setContentCategories(Array.isArray(cRes.data) ? cRes.data : []);
      setContentTags(Array.isArray(tRes.data) ? tRes.data : []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit, status: statusFilter };
      if (categoryFilter) params.category = categoryFilter;
      if (tagFilter) params.tag = tagFilter;
      if (searchTerm) params.search = searchTerm;
      const res = await api.get('/blogs', { params });
      setBlogs(res.data.blogs || []);
      setPagination(res.data.pagination || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  /* New state for image file */
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  /* Update openAdd/openEdit to reset/set preview */
  const openAdd = () => {
    setEditingId(null);
    setFormData({ title: '', content: '', excerpt: '', image: '', contentCategory: '', contentTags: [], author: '', status: 'draft', featured: false });
    setSelectedTagIds([]);
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      setFormLoading(true);
      const res = await api.get(`/blogs/admin/${id}`);
      const b = res.data;
      setFormData({
        title: b.title || '',
        content: b.content || '',
        excerpt: b.excerpt || '',
        image: b.image || '',
        contentCategory: (b.contentCategory && (b.contentCategory._id || b.contentCategory)) || '',
        contentTags: [],
        author: b.author || '',
        status: b.status || 'draft',
        featured: !!b.featured,
      });
      setSelectedTagIds((b.contentTags || []).map((t) => (t._id || t).toString()));
      setEditingId(id);
      setImageFile(null);
      setImagePreview(b.image || null);
      setShowModal(true);
    } catch (e) {
      showError(e.response?.data?.message || 'Error loading');
    } finally {
      setFormLoading(false);
    }
  };

  /* Handle file selection */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      // Clear manual URL input if a file is selected, or keep it if we want to allow mixed usage. 
      // For now, let's assume file upload takes precedence.
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        contentCategory: formData.contentCategory || undefined,
        contentTags: selectedTagIds,
      };

      let blogId = editingId;

      if (editingId) {
        await api.put(`/blogs/${editingId}`, payload);
        showSuccess('Blog updated.');
      } else {
        const res = await api.post('/blogs', payload);
        blogId = res.data._id;
        showSuccess('Blog created.');
      }

      // Upload image if selected
      if (imageFile && blogId) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);

        try {
          await api.post(`/blogs/${blogId}/image`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          showSuccess('Image uploaded successfully.');
        } catch (imgErr) {
          console.error('Image upload failed', imgErr);
          showError('Blog saved but image upload failed.');
        }
      }

      closeModal();
    } catch (e) {
      showError(e.response?.data?.message || 'Error saving');
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    fetchBlogs();
  };

  const handleTagToggle = (id) => {
    setSelectedTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      showError('Category name is required');
      return;
    }
    setCreatingCategory(true);
    try {
      const res = await api.post('/content-categories', {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined,
        active: true,
      });
      await fetchDropdowns();
      setFormData((prev) => ({ ...prev, contentCategory: res.data._id }));
      setShowCreateCategoryModal(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      showSuccess('Content category created.');
    } catch (e) {
      showError(e.response?.data?.message || 'Error creating category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      showError('Tag name is required');
      return;
    }
    setCreatingTag(true);
    try {
      const res = await api.post('/content-tags', {
        name: newTagName.trim(),
        active: true,
      });
      await fetchDropdowns();
      setSelectedTagIds((prev) => [...prev, res.data._id]);
      setShowCreateTagModal(false);
      setNewTagName('');
      showSuccess('Content tag created.');
    } catch (e) {
      showError(e.response?.data?.message || 'Error creating tag');
    } finally {
      setCreatingTag(false);
    }
  };

  const handleDelete = (id) => {
    showConfirm('Delete this blog?', async () => {
      try {
        await api.delete(`/blogs/${id}`);
        showSuccess('Deleted.');
        fetchBlogs();
      } catch (e) {
        showError(e.response?.data?.message || 'Error deleting');
      }
    });
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  return (
    <>
      <SEO title="Blogs – Admin" noindex />
      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-50">Blogs</h1>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowFbModal(true);
                  setFbSuccessResult(null);
                  setFbUrl('');
                }}
                className="px-4 py-2 bg-[#1877F2] hover:bg-[#166fe5] text-white text-sm font-semibold rounded-lg shadow-sm flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Sparkles className="w-4 h-4" />
                Import from Facebook
              </button>
              <button type="button" onClick={openAdd} className="btn-primary text-sm cursor-pointer">
                Add Blog
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search..."
              className="flex-1 min-w-[180px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            />
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
              <option value="all">All status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
              <option value="">All categories</option>
              {contentCategories.map((c) => (
                <option key={c._id} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <select value={tagFilter} onChange={(e) => { setTagFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
              <option value="">All tags</option>
              {contentTags.map((t) => (
                <option key={t._id} value={t.slug}>{t.name}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <PageLoader message="Loading..." />
          ) : (
            <>
              <div className="card overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Featured</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {blogs.map((b) => (
                      <tr key={b._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-50">{b.title}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{b.contentCategory?.name || '–'}</td>
                        <td className="px-4 py-3"><span className={`badge text-xs ${b.status === 'published' ? 'badge-success' : 'badge-warning'}`}>{b.status}</span></td>
                        <td className="px-4 py-3">{b.featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" /> : '–'}</td>
                        <td className="px-4 py-3">
                          <button type="button" onClick={() => openEdit(b._id)} className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1 mr-3"><Pencil className="w-4 h-4" /> Edit</button>
                          <button type="button" onClick={() => handleDelete(b._id)} className="text-error-600 dark:text-error-400 hover:underline inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(!blogs || blogs.length === 0) && <div className="card p-12 text-center text-gray-600 dark:text-gray-400">No blogs.</div>}
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    itemsPerPage={pagination.itemsPerPage}
                    itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                    onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  />
                </div>
              )}
            </>
          )}

          {/* Facebook Import Modal */}
          {showFbModal && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-gray-100 dark:border-gray-700">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center text-white">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-white/20 rounded-lg">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold leading-tight">Import from Facebook</h2>
                      <p className="text-xs text-blue-100">Auto-transcribe video/reels, OCR images &amp; generate SEO blog</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => !fbLoading && setShowFbModal(false)}
                    className="text-white/80 hover:text-white text-2xl leading-none cursor-pointer"
                    disabled={fbLoading}
                  >
                    ×
                  </button>
                </div>

                <div className="p-6 space-y-5">
                  {fbSuccessResult ? (
                    <div className="space-y-4 text-center py-4">
                      <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle className="w-8 h-8" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">Blog Successfully Published!</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 font-medium line-clamp-2 px-4">
                        {fbSuccessResult.title}
                      </p>
                      <div className="flex items-center justify-center gap-3 pt-2">
                        <a
                          href={`/blog/${fbSuccessResult.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors"
                        >
                          View Live Blog <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            setShowFbModal(false);
                            openEdit(fbSuccessResult._id);
                          }}
                          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                        >
                          Edit in Admin
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFbSuccessResult(null);
                            setFbUrl('');
                          }}
                          className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline cursor-pointer"
                        >
                          Import Another
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1.5">
                          Facebook Post or Reel URL *
                        </label>
                        <input
                          type="url"
                          value={fbUrl}
                          onChange={(e) => setFbUrl(e.target.value)}
                          placeholder="https://www.facebook.com/reel/1568067092009570/ or post URL"
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                          disabled={fbLoading}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Supports Facebook Reels, Video links, or Photo/News posts.
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Publish Status
                          </label>
                          <select
                            value={fbStatus}
                            onChange={(e) => setFbStatus(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            disabled={fbLoading}
                          >
                            <option value="published">Published (Live)</option>
                            <option value="draft">Draft (Review later)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            AI Processing
                          </label>
                          <div className="text-xs text-gray-500 dark:text-gray-400 py-2">
                            Gemini 3.5 Verbatim Speech + Caption
                          </div>
                        </div>
                      </div>

                      {fbLoading && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 text-center space-y-2">
                          <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin mx-auto" />
                          <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                            {fbStep || 'Processing Facebook media...'}
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-300">
                            Downloading video, transcribing spoken interview, and generating in-depth article. Please wait ~20-30 seconds.
                          </p>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <button
                          type="button"
                          onClick={() => setShowFbModal(false)}
                          disabled={fbLoading}
                          className="w-full sm:w-auto px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFbImport(true)}
                          disabled={fbLoading || !fbUrl.trim()}
                          className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-semibold transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          ✍ Generate &amp; Load in Editor
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFbImport(false)}
                          disabled={fbLoading || !fbUrl.trim()}
                          className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                        >
                          {fbLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Auto-Generate &amp; Publish
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {showModal && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8">
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-50">{editingId ? 'Edit Blog' : 'Add Blog'}</h2>
                  <button type="button" onClick={closeModal} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Title *</label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="input-field" />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Excerpt</label>
                    <textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} rows={2} className="input-field" />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Featured Image</label>

                    {imagePreview && (
                      <div className="mb-3 relative w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => { setImageFile(null); setImagePreview(null); setFormData({ ...formData, image: '' }); }}
                          className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 shadow-md"
                          title="Remove Image"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.image}
                        onChange={(e) => { setFormData({ ...formData, image: e.target.value }); if (!imageFile) setImagePreview(e.target.value); }}
                        placeholder="Enter Image URL or Upload File"
                        className="input-field flex-1"
                        disabled={!!imageFile}
                      />
                      <div className="relative">
                        <input
                          type="file"
                          id="blog-image-upload"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="blog-image-upload"
                          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center gap-2 h-full whitespace-nowrap"
                        >
                          <Pencil className="w-4 h-4" /> Upload
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Paste a URL or upload an image file.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Category</label>
                      <div className="flex gap-2">
                        <select value={formData.contentCategory} onChange={(e) => setFormData({ ...formData, contentCategory: e.target.value })} className="input-field flex-1">
                          <option value="">None</option>
                          {contentCategories.map((c) => (
                            <option key={c._id} value={c._id}>{c.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowCreateCategoryModal(true)}
                          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors whitespace-nowrap"
                          title="Create New Content Category"
                        >
                          + New
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Author</label>
                      <input type="text" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} className="input-field" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold">Tags</label>
                      <button
                        type="button"
                        onClick={() => setShowCreateTagModal(true)}
                        className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors"
                        title="Create New Content Tag"
                      >
                        + New Tag
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {contentTags.map((t) => (
                        <button
                          key={t._id}
                          type="button"
                          onClick={() => handleTagToggle(t._id)}
                          className={`px-3 py-1.5 rounded-full text-sm ${selectedTagIds.includes((t._id || t).toString()) ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Status</label>
                      <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="input-field">
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 self-end">
                      <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="rounded" />
                      <span className="text-gray-700 dark:text-gray-300">Featured</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Content *</label>
                    <div className="rich-text-editor">
                      <ReactQuill
                        theme="snow"
                        value={formData.content}
                        onChange={(v) => setFormData({ ...formData, content: v })}
                        modules={quillModules}
                        placeholder="Write content..."
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button type="submit" disabled={formLoading} className="btn-primary flex-1">{(formLoading ? 'Saving...' : (editingId ? 'Update' : 'Create'))}</button>
                    <button type="button" onClick={closeModal} className="btn-secondary flex-1">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Create Content Category Modal */}
          {showCreateCategoryModal && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-50">Create Content Category</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Enter category name"
                      className="input-field"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Description (Optional)</label>
                    <textarea
                      value={newCategoryDescription}
                      onChange={(e) => setNewCategoryDescription(e.target.value)}
                      placeholder="Enter description"
                      rows={3}
                      className="input-field"
                    />
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      disabled={creatingCategory || !newCategoryName.trim()}
                      className="btn-primary flex-1 disabled:opacity-50"
                    >
                      {creatingCategory ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCreateCategoryModal(false); setNewCategoryName(''); setNewCategoryDescription(''); }}
                      disabled={creatingCategory}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Content Tag Modal */}
          {showCreateTagModal && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-50">Create Content Tag</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Enter tag name"
                      className="input-field"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={handleCreateTag}
                      disabled={creatingTag || !newTagName.trim()}
                      className="btn-primary flex-1 disabled:opacity-50"
                    >
                      {creatingTag ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCreateTagModal(false); setNewTagName(''); }}
                      disabled={creatingTag}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

const AdminBlogs = () => (
  <AdminLayout>
    <AdminBlogsContent />
  </AdminLayout>
);

export default AdminBlogs;
